// Content for the Diamondbrite (Jewelultra Ltd) case study — Boost! portfolio piece

// Real Diamondbrite brand imagery (hosted in /public/diamondbrite), extracted
// from the client's own marketing materials & website.
export const DB_IMAGES = {
  hero: '/diamondbrite/hero.jpg',
  store: '/diamondbrite/store.jpg',
  stand: '/diamondbrite/stand.jpg',
  products: '/diamondbrite/products.jpg',
};

export const BOOST_LOGO =
  'https://customer-assets-lqy194kg.emergentagent.net/job_31bcde7c-d98a-4c27-a3ab-d96f08806001/artifacts/e3mvklhv_Logo%20Pink.png';

export const DB_HERO = {
  eyebrow: 'Boost! · Case Study',
  chips: ['Reputation', 'Ecommerce', 'Brand', 'Full-Funnel Marketing'],
  year: '2022–2025',
  title: 'From 2.8★ to 4.9★ — a reputation transformed',
  sub:
    'A multi-year strategic alliance with Diamondbrite (Jewelultra Ltd) — turning a quiet reputation into one of the best-rated names in UK vehicle care, and building the ecommerce, brand and marketing engine behind it.',
  client: 'Jewelultra Ltd — Diamondbrite',
  liveUrl: 'https://www.diamondbrite.co.uk',
};

export const DB_CLIENT = {
  kicker: '01 — The Client',
  title: 'One of the UK\u2019s leading names in vehicle care',
  copy: [
    'Diamondbrite, from parent company Jewelultra Ltd, is one of the UK\u2019s leading names in vehicle care and paint protection \u2014 trusted by dealerships, detailers and drivers for decades.',
    'What began as creative support has evolved into a true strategic alliance. We don\u2019t act like an agency \u2014 we act like part of the team, and Diamondbrite\u2019s wins are our wins.',
  ],
  facts: [
    { value: 'Jewelultra', label: 'Parent company' },
    { value: 'Vehicle care', label: 'Paint protection · UK' },
    { value: '2022\u20132025', label: 'Ongoing partnership' },
    { value: 'Full-service', label: 'Brand · web · marketing' },
  ],
};

export const DB_CHALLENGE = {
  kicker: '02 — The Challenge',
  title: 'A respected product line that the numbers didn\u2019t reflect',
  items: [
    {
      icon: 'Star',
      title: 'A reputation stuck in neutral',
      copy: 'Just 4 Google reviews at a 2.8\u2605 average in 2022 \u2014 nowhere near the trust the products had earned in the real world.',
    },
    {
      icon: 'Camera',
      title: 'Expensive product imagery',
      copy: 'Traditional photography for a growing catalogue was slow and costly, holding back store updates and campaigns.',
    },
    {
      icon: 'ShoppingCart',
      title: 'An ecommerce experience to modernise',
      copy: 'The online store needed loyalty, automation and a smoother journey to keep pace with the brand\u2019s ambition.',
    },
    {
      icon: 'Megaphone',
      title: 'Marketing without a system',
      copy: 'Great products, but no joined-up engine turning attention into reviews, repeat sales and long-term loyalty.',
    },
  ],
};

export const DB_APPROACH = {
  kicker: '03 — Our Approach',
  title: 'Behave like part of the team \u2014 then build the growth engine',
  items: [
    {
      icon: 'Star',
      title: 'Automate reputation',
      copy: 'An automated review-invitation system with a thank-you discount after every purchase \u2014 and a personalised reply to every review.',
    },
    {
      icon: 'Gem',
      title: 'Elevate the brand',
      copy: 'A modern brand website, event & POS design, chrome-effect labels and photo-realistic 3D renders that replace costly photography.',
    },
    {
      icon: 'ShoppingCart',
      title: 'Rebuild ecommerce',
      copy: 'Loyalty & rewards, DHL fulfilment automation, an AI chatbot and a dynamic store with seasonal, real-time promotions.',
    },
    {
      icon: 'Megaphone',
      title: 'Run full-funnel marketing',
      copy: 'Email journeys, social strategy & content, SEO-rich YouTube and Google & Facebook Ads \u2014 all measured and tuned.',
    },
  ],
};

export const DB_SOLUTION = {
  kicker: '04 — The Solution',
  title: 'A complete brand, store and marketing engine \u2014 run as one',
  features: [
    {
      icon: 'Star',
      title: 'Automated review engine',
      copy: 'Every purchase triggers a review invitation with a thank-you discount \u2014 taking Diamondbrite from 4 reviews to 422+, each with a personalised reply that compounds SEO and trust.',
    },
    {
      icon: 'ShoppingCart',
      title: 'Ecommerce that performs',
      copy: 'A points & rewards loyalty scheme, DHL fulfilment automation, a TreeNation sustainability partnership and an AI chatbot that saves hours of office time every week.',
    },
    {
      icon: 'Gem',
      title: 'A modern brand identity',
      copy: 'A dedicated brand website with ongoing optimisation, event & POS design, chrome-effect Graphene labels and new training certificates.',
    },
    {
      icon: 'Camera',
      title: 'Photo-realistic 3D renders',
      copy: 'Product renders that saved thousands in photography costs \u2014 crisp, consistent imagery ready for store, ads and print, on demand.',
    },
    {
      icon: 'Megaphone',
      title: 'Full-funnel marketing',
      copy: 'Email campaigns with measurable sales impact, welcome journeys with coupons, social strategy & content, SEO-rich YouTube and Google & Facebook Ads.',
    },
    {
      icon: 'BadgeCheck',
      title: 'Product identity, led by us',
      copy: 'Boost! independently created the NanoCeramic logo for the Advanced Ceramic line \u2014 now adopted across all Diamondbrite packaging and marketing.',
    },
  ],
  gallery: {
    label: 'The brand, the store and the results',
    shots: [
      { src: DB_IMAGES.store, caption: 'A dynamic, seasonal online store' },
      { src: DB_IMAGES.products, caption: 'Photo-realistic product renders' },
      { src: DB_IMAGES.stand, caption: 'Event & POS brand presence' },
    ],
  },
};

export const DB_RESULTS = {
  kicker: '05 — The Results',
  title: 'A brand in full orbit \u2014 measured in reviews, sales and time saved',
  headline: [
    { value: '4.9', unit: '\u2605', label: 'Google rating', note: 'up from 2.8\u2605 in 2022' },
    { value: '105', unit: 'x', label: 'More Google reviews', note: '4 \u2192 422+ verified reviews' },
    { value: '422', unit: '+', label: 'Five-star-led reviews', note: 'each with a personalised reply' },
    { value: '\u00a31,000s', unit: '', label: 'Saved on photography', note: 'via photo-realistic 3D renders' },
  ],
  groups: [
    {
      icon: 'Star',
      title: 'Reputation & SEO',
      points: [
        'From 4 reviews at 2.8\u2605 to 422+ at 4.9\u2605 \u2014 among the best in the industry.',
        'Automated invitations with a thank-you discount after every purchase.',
        'A personalised reply to every review, compounding SEO visibility and trust.',
      ],
    },
    {
      icon: 'ShoppingCart',
      title: 'Ecommerce & automation',
      points: [
        'Points & rewards loyalty scheme driving repeat purchases.',
        'DHL fulfilment automation and an AI chatbot saving hours every week.',
        'Dynamic store with seasonal promotions and real-time updates.',
      ],
    },
    {
      icon: 'Megaphone',
      title: 'Brand & marketing',
      points: [
        'Chrome-effect Graphene labels, event/POS design and 3D renders.',
        'Email journeys, social content and SEO-rich YouTube optimisation.',
        'Google & Facebook Ads management with measurable sales impact.',
      ],
    },
  ],
};

export const DB_TECH = [
  'WordPress', 'WooCommerce', 'Review Automation', 'AI Chatbot', '3D Product Renders',
  'Email Marketing', 'Google & Meta Ads', 'SEO', 'DHL Automation', 'Loyalty & Rewards',
];

export const DB_TESTIMONIAL = {
  // Boost!'s own partnership positioning statement (not a client quote).
  quote:
    'We don\u2019t act like an agency \u2014 we act like part of the team. Diamondbrite\u2019s wins are our wins: a stronger brand, stronger sales, and a stronger future.',
  name: 'The Boost! team',
  role: 'On our Diamondbrite partnership',
  photo: '',
};

export const DB_CTA = {
  title: "Got a brand that's ready for full orbit?",
  sub: "Let's build the reputation, store and marketing engine to get it there.",
  email: 'team@boostmarketing.agency',
  siteUrl: 'https://boostmarketing.agency/#contact',
};
